import { inject, Injectable } from '@angular/core';
import { StorageService, UserService } from '@shared/services/index';
import { StorageType } from '../../shared/enums';
import { Auth } from '@angular/fire/auth';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { environment } from '@environments/environment';
import { AuthenticationService } from '../../pages/services/authentication.service';
import { MatDialog } from '@angular/material/dialog';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private tokenKey = '_sophia_token';
  private expKey = '_sophia_token_exp';

  private warningTimeout: any;
  private logoutTimeout: any;
  public sessionWarning$ = new Subject<void>();
  public sessionLogout$ = new Subject<void>();

  private readonly authService = inject(AuthenticationService);
  private readonly userService = inject(UserService);
  private readonly storage = inject(StorageService);
  private readonly auth = inject(Auth);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);

  logout(): void {
    clearTimeout(this.warningTimeout);
    clearTimeout(this.logoutTimeout);
    this.dialog.closeAll();
    this.sessionLogout$.next();
    this.authService.reset();
    this.userService.reset();
    this.storage.clear(StorageType.SESSION);
    this.storage.clear(StorageType.LOCAL);
    this.deleteSession();
    this.auth.signOut();
    this.router.navigate(['login'], { replaceUrl: true });
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    const exp = this.getTokenExpiration();
    return !!token && !!exp && exp > Date.now() / 1000;
  }

  setSession(token: string) {
    const exp = this.getTokenExpirationFromJWT(token);

    if (exp) {
      this.setCookie(this.tokenKey, token, exp);
      this.setCookie(this.expKey, exp.toString(), exp);

      const msToExpire = exp * 1000 - Date.now();
      const msToWarning = msToExpire - environment.session.remainingTimeMinutes * 60 * 1000;

      clearTimeout(this.warningTimeout);
      clearTimeout(this.logoutTimeout);

      this.warningTimeout = setTimeout(() => {
        this.sessionWarning$.next();
      }, msToWarning);

      this.logoutTimeout = setTimeout(() => {
        this.logout();
      }, msToExpire);
    }
  }

  deleteSession() {
    this.removeToken();
  }

  getToken(): string | null {
    return this.getCookie(this.tokenKey);
  }

  getTokenExpiration(): number | null {
    const exp = this.getCookie(this.expKey);
    return exp ? parseInt(exp, 10) : null;
  }

  removeToken() {
    this.deleteCookie(this.tokenKey);
    this.deleteCookie(this.expKey);
  }

  restoreSessionTimers() {
    const token = this.getToken();
    const exp = this.getTokenExpiration();

    if (token && exp) {
      const msToExpire = exp * 1000 - Date.now();
      const msToWarning = msToExpire - environment.session.remainingTimeMinutes * 60 * 1000;

      clearTimeout(this.warningTimeout);
      clearTimeout(this.logoutTimeout);

      if (msToExpire <= 0) {
        this.logout();
        return;
      }

      if (msToWarning <= 0) {
        this.sessionWarning$.next();

        this.logoutTimeout = setTimeout(() => {
          this.logout();
        }, msToExpire);
        return;
      }

      this.warningTimeout = setTimeout(() => {
        this.sessionWarning$.next();
      }, msToWarning);

      this.logoutTimeout = setTimeout(() => {
        this.logout();
      }, msToExpire);
    }
  }

  getRemainingSessionTimeMs(): number {
    const exp = this.getTokenExpiration();
    return exp ? exp * 1000 - Date.now() : 0;
  }

  private setCookie(name: string, value: string, exp: number) {
    const expires = new Date(exp * 1000).toUTCString();
    document.cookie = `${name}=${value}; expires=${expires}; path=/; Secure; SameSite=Strict`;
  }

  private getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? match[2] : null;
  }

  private deleteCookie(name: string) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  }

  private getTokenExpirationFromJWT(token: string): number | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp || null;
    } catch {
      return null;
    }
  }
}
